import { observable } from 'mobx';
import type { OrderItemResponseArgs } from './order_item2';
import OrderItem, { convertOrderItemResponseToArgs } from './order_item2';

export type OrderResponseArgs = {
  id: number;
  order_items?: OrderItemResponseArgs[];
  traveler_confirmation_status?: string;
};

export type OrderArgs = {
  id: number;
  showFee: boolean;
  orderItems: OrderItem[];
  travelerConfirmationStatus?: string;
};

export const convertOrderResponseToArgs = (response: OrderResponseArgs, showFee: boolean) => {
  const args: OrderArgs = {
    id: response.id,
    showFee,
    orderItems:
      response.order_items?.map(orderItem => new OrderItem(convertOrderItemResponseToArgs(orderItem, showFee))) ||
      [],
    travelerConfirmationStatus: response.traveler_confirmation_status
  };
  return args;
};

export default class Order {
  @observable id: number;
  @observable showFee: boolean;
  @observable orderItems: OrderItem[];
  @observable travelerConfirmationStatus?: string;

  constructor(args: OrderArgs) {
    this.id = args.id;
    this.showFee = args.showFee;
    this.orderItems = args.orderItems;
    this.travelerConfirmationStatus = args.travelerConfirmationStatus;
  }

  get startDate() {
    const dates = this.orderItems.map(item => item.startDate).filter(Boolean);

    if (dates.length === 0) return null;

    return dates.sort((a, b) => (a?.toDate()?.getTime() ?? 0) - (b?.toDate()?.getTime() ?? 0))[0];
  }

  get endDate() {
    const dates = this.orderItems.map(item => item.endDate).filter(Boolean);

    if (dates.length === 0) return null;

    return dates.sort((a, b) => (a?.toDate()?.getTime() ?? 0) - (b?.toDate()?.getTime() ?? 0))[dates.length - 1];
  }

  get totalPrice() {
    return this.orderItems.reduce((acc, item) => acc + (item.price?.totalPrice ?? 0), 0);
  }

  get totalBufferAmount() {
    return this.orderItems.reduce((acc, item) => acc + (item.bufferAmount ?? 0), 0);
  }

  get totalPriceWithBuffer() {
    return this.orderItems.reduce((acc, item) => acc + (item.price?.budgetFrameAmount ?? 0), 0);
  }

  get isTicketingExpired() {
    return this.orderItems.some(item => item.isTicketingExpired);
  }
}
